/**
 * T-045 Settings → Client portal: the certificate-of-insurance requirements (superadmin).
 * GET returns what is saved plus the approved values Settings offers to fill in; PUT saves.
 */
import { adminFetch } from '@/lib/adminApi';

export type EntityKey = 'california' | 'illinois' | 'louisiana';
export type CoverageKey = 'general_liability' | 'auto_liability' | 'hired_physical_damage' | 'property_equipment' | 'workers_comp' | 'certificate';

/** A legal name and address on a certificate. */
export interface CoiParty { legal_name: string; address: string }
/**
 * One entity's entry: the certificate holder, plus who else. null = the holder (L1, Eric via
 * Master 2026-10-29: each its own setting).
 */
export interface CoiHolder extends CoiParty { additional_insured: CoiParty | null; loss_payee: CoiParty | null }
export type CoverageText = Record<CoverageKey, string>;

export interface CoiConfig {
  holders: Record<EntityKey, CoiHolder | null>;
  limits: { default: CoverageText; by_region: Record<string, CoverageText> };
}

/** One of the three Hollywood Trucks companies, to pick as a holder, insured or payee. */
export interface CoiPartyOption extends CoiParty { key: EntityKey }

export interface CoiSettingsResponse {
  config: CoiConfig;
  suggested: { holders: Record<EntityKey, CoiParty | null>; limits: CoverageText };
  entities: { key: EntityKey; label: string }[];
  parties: CoiPartyOption[];
  fields: { key: CoverageKey; label: string }[];
  regions: { id: string; name: string }[];
  regions_unavailable?: boolean;
}

/** A holder in a PUT: the holder pair, plus only the other parties that changed (left out = kept). */
export type CoiHolderPatch = CoiParty & Partial<Pick<CoiHolder, 'additional_insured' | 'loss_payee'>>;

export interface CoiSettingsPatch {
  holders?: Partial<Record<EntityKey, CoiHolderPatch | null>>;
  limits?: { default?: Partial<CoverageText>; by_region?: Record<string, Partial<CoverageText>> };
}

export function getCoiSettings(): Promise<CoiSettingsResponse> {
  return adminFetch<CoiSettingsResponse>('/portal/settings/coi');
}

export function saveCoiSettings(patch: CoiSettingsPatch): Promise<{ config: CoiConfig }> {
  return adminFetch<{ config: CoiConfig }>('/portal/settings/coi', { method: 'PUT', body: JSON.stringify(patch) });
}
